import styled from 'styled-components';

export const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 6px 8px;
  border-radius: 6px;
  border: 1px solid
    ${(p) =>
      ((p.theme as { colors?: Record<string, string | undefined> }).colors
        ?.outline ?? '#3a3a3a')};
  background: ${(p) =>
    ((p.theme as { colors?: Record<string, string | undefined> }).colors
      ?.surface ?? 'transparent')};
  font-size: 11px;
  min-width: 0;
`;

export const TopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

export const Name = styled.a`
  flex: 1;
  min-width: 0;
  color: inherit;
  text-decoration: none;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover,
  &:focus-visible {
    text-decoration: underline;
  }
`;

export const Meta = styled.span`
  font-size: 10px;
  opacity: 0.7;
  flex-shrink: 0;
`;

export const PreviewBox = styled.div`
  position: relative;
  width: 100%;
  border-radius: 4px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.25);
`;

/**
 * Image artifact thumbnail. Scales proportionally (width:100%, height:auto)
 * so the natural aspect ratio is preserved — no cropping or letter-boxing.
 * Alignment of mixed-artifact strips is achieved via the shared {@link PreviewBox}
 * width / surrounding card container, not a fixed thumbnail height.
 */
export const Thumb = styled.img`
  display: block;
  width: 100%;
  height: auto;
  cursor: zoom-in;
`;

export const ModelPreviewSurface = styled.div`
  width: 100%;
  height: 140px;
  background: ${(p) =>
    ((p.theme as { colors?: Record<string, string | undefined> }).colors
      ?.surfaceVariant ?? '#13131b')};

  /* The shared ModelViewer renders a full-bleed canvas; force it to fill the
     fixed-height surface even when the parent doesn't have an explicit height
     itself. Same trick the chat ArtifactCard uses for its model preview. */
  & > * {
    width: 100%;
    height: 100%;
  }
`;

export const DocumentPreviewSurface = styled.div`
  width: 100%;
  height: 140px;
`;

export const ExpandButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 2;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  cursor: pointer;
  opacity: 0.85;
  transition:
    opacity 0.12s,
    background 0.12s;

  &:hover,
  &:focus-visible {
    opacity: 1;
    background: rgba(0, 0, 0, 0.75);
  }
`;

export const ModelLoadingSurface = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 140px;
  font-size: 11px;
  color: ${(p) =>
    ((p.theme as { colors?: Record<string, string | undefined> }).colors
      ?.onSurfaceVariant ?? '#A0A0A0')};
`;

export const Placeholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 60px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.18);
  font-size: 18px;
  color: ${(p) =>
    ((p.theme as { colors?: Record<string, string | undefined> }).colors
      ?.onSurfaceVariant ?? '#A0A0A0')};
`;

// Read-only preview for TEXT/NUMBER artifacts — the text-input equivalent
// of {@link Thumb} (image) and the 3D viewer. Min-height keeps short
// strings from collapsing to nothing; resizable vertically so a longer
// paragraph isn't trapped behind a tiny scrollbar.
export const TextPreviewBox = styled.textarea`
  display: block;
  width: 100%;
  min-height: 80px;
  max-height: 200px;
  padding: 8px 10px;
  margin: 0;
  border-radius: 4px;
  border: 1px solid
    ${(p) =>
      ((p.theme as { colors?: Record<string, string | undefined> }).colors
        ?.outline ?? '#3a3a3a')};
  background: rgba(0, 0, 0, 0.25);
  color: ${(p) =>
    ((p.theme as { colors?: Record<string, string | undefined> }).colors
      ?.onSurface ?? '#E6E6F2')};
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 11px;
  line-height: 1.5;
  resize: vertical;
  cursor: text;

  &:focus-visible {
    outline: none;
    border-color: ${(p) =>
      ((p.theme as { colors?: Record<string, string | undefined> }).colors
        ?.primary ?? '#7B68EE')};
  }
`;

export const TextLoadingSurface = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 60px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.18);
  font-size: 11px;
  color: ${(p) =>
    ((p.theme as { colors?: Record<string, string | undefined> }).colors
      ?.onSurfaceVariant ?? '#A0A0A0')};
`;
